import { useSWR, useSWRMutation } from '@tiger/swr';
import { type FC, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ErrorBox from 'components/ErrorBox';
import Loading from 'components/Loading';
import Toast from 'components/Toast';
import security from 'stores/security';
import API from 'utils/API';
import http from 'utils/http';
import { useBankCardList } from '../../hooks/useBankCardList';
import RecurringConfig, { type RecurringConfigData, type RecurringConfigProps } from '../components/RecurringConfig';
import { PeriodPlanListItem } from '../types';

type Props = Pick<RecurringConfigProps, 'isIRA'>;

const Edit: FC<Props> = ({ isIRA }) => {
    const locationState = (useLocation().state ?? {}) as { next?: string };
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const { isLoading, data, error, mutate } = useSWR<PeriodPlanListItem>(
        {
            url: API.TRADE.funding.getDepositAchPlanDetail(id!),
            params: {
                account_id: security.currentAccountId,
                customer_id: security.currentCustomerId
            }
        },
        {
            dataChain: resp => resp.data,
            immutable: true
        }
    );

    // 银行卡列表
    const {
        data: bankCardList,
        isLoading: isBankCardListLoading,
        error: bankCardListError,
        mutate: mutateBankCardList
    } = useBankCardList();

    const initialValues = useMemo<Partial<RecurringConfigData>>(() => {
        // 不同卡 id 字段不同
        // source = Bank 时为 cardId
        // source = Fund 时为 fundCardId
        const bankCardInfo = bankCardList?.find(item => item.id === (data?.cardId ?? data?.fundCardId));

        return {
            account_id: security.currentAccountId,
            amount: data?.amount,
            ach_card_id: bankCardInfo?.id,
            period_type: data?.periodType,
            deposit_customer_id: security.currentCustomerId
        };
    }, [data, bankCardList]);

    const { trigger: handleEdit } = useSWRMutation(
        {
            url: API.TRADE.funding.modifyDepositAchPlan(id!),
            params: {
                account_id: security.currentAccountId,
                customer_id: security.currentCustomerId
            }
        },
        async (params: RecurringConfigData) => {
            await http.post(API.TRADE.funding.modifyDepositAchPlan(id!), params, {
                useForm: true,
                params: {
                    account_id: security.currentAccountId,
                    customer_id: security.currentCustomerId
                }
            });
        },
        {
            onError: err => Toast.show(err),
            onSuccess: () => {
                Toast.show(__('修改成功'));
                navigate(locationState?.next ?? '/profile/deposit/ach/recurring/schedules');
            }
        }
    );

    if ((bankCardListError && !bankCardList) || (error && !data)) {
        return (
            <ErrorBox
                error={error || bankCardListError}
                onClick={() => {
                    error && mutate();
                    bankCardListError && mutateBankCardList();
                }}
            />
        );
    }

    if (isBankCardListLoading || isLoading) {
        return <Loading />;
    }

    return (
        <RecurringConfig
            isEdit
            isIRA={isIRA}
            bankCardList={bankCardList}
            initialValues={initialValues}
            onSubmit={handleEdit}
        />
    );
};

export default Edit;
